import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import { DealPermissionsEditor } from "@/src/components/permissions/DealPermissionsEditor";
import { permissionsToInput } from "@/src/components/permissions/dealPermissionConfig";
import { Button } from "@/src/components/tailwind/Button";
import { TextInput } from "@/src/components/tailwind/TextInput";
import { toasts } from "@/src/components/toasts/toasts";
import {
  DealRolePermissions,
  DealRolePermissionsInput,
  DealRoleType,
  useUpdateDealRoleMutation,
  useUpdateDealRolePermissionsMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export type EditableDealRole = {
  id: string;
  name: string;
  description: string;
  type: DealRoleType;
  permissionLevels: DealRolePermissions;
};

function permissionsEqual(
  a: DealRolePermissionsInput,
  b: DealRolePermissionsInput,
) {
  return (
    a.documents === b.documents &&
    a.questions === b.questions &&
    a.adminAccess === b.adminAccess &&
    a.guestAccess === b.guestAccess &&
    a.dealAssistant === b.dealAssistant &&
    a.dealActivity === b.dealActivity &&
    a.dealSettings === b.dealSettings
  );
}

export function EditDealRoleModal(props: {
  open: boolean;
  onClose: () => void;
  role: EditableDealRole;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updateRole = useUpdateDealRoleMutation(client);
  const updatePermissions = useUpdateDealRolePermissionsMutation(client);

  const [name, setName] = useState(props.role.name);
  const [permissions, setPermissions] = useState<DealRolePermissionsInput>(
    () => permissionsToInput(props.role.permissionLevels),
  );

  useEffect(() => {
    if (!props.open) {
      return;
    }
    setName(props.role.name);
    setPermissions(permissionsToInput(props.role.permissionLevels));
  }, [props.open, props.role]);

  const initialPermissions = permissionsToInput(props.role.permissionLevels);
  const nameChanged = name.trim() !== props.role.name;
  const permissionsChanged = !permissionsEqual(permissions, initialPermissions);
  const canSave =
    name.trim().length > 0 && (nameChanged || permissionsChanged);
  const isSaving = updateRole.isPending || updatePermissions.isPending;

  async function handleSave() {
    if (!canSave || isSaving) {
      return;
    }

    try {
      if (nameChanged) {
        await updateRole.mutateAsync({
          input: {
            id: props.role.id,
            name: name.trim(),
          },
        });
      }

      if (permissionsChanged) {
        await updatePermissions.mutateAsync({
          input: {
            dealRoleId: props.role.id,
            permissions,
          },
        });
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["DealRoles"] }),
        queryClient.invalidateQueries({ queryKey: ["Firm"] }),
      ]);
      toasts.success("Role updated");
      props.onClose();
    } catch {
      toasts.error("Failed to update role");
    }
  }

  return (
    <AnimatedModal open={props.open} onClose={props.onClose} size="lg">
      <div className="flex items-center justify-between mb-4">
        <p className="font-semibold text-foreground">Edit role</p>
        <CloseIcon onClose={props.onClose} />
      </div>

      <TextInput
        label="Name"
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
        placeholder="Role name"
      />

      <div className="mt-4">
        <p className="text-sm font-semibold text-persian-990 mb-1">
          Permissions
        </p>
        <DealPermissionsEditor
          permissions={permissions}
          roleType={props.role.type}
          onChange={setPermissions}
          disabled={isSaving}
        />
      </div>

      <div className="mt-6 flex justify-end gap-2">
        <Button text="Cancel" variant="neutral" onClick={props.onClose} />
        <Button
          text="Save"
          variant="positive"
          isLoading={isSaving}
          isDisabled={!canSave}
          onClick={handleSave}
        />
      </div>
    </AnimatedModal>
  );
}
